import Flex from 'common/components/Flex'
import Typography from 'common/components/Typography'
import theme from 'common/theme'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

const setBorder = (bordered, borderActiveColor, error) => {
  const borderColor = error
    ? theme.colors.red
    : theme.colors.grayLight
  switch (bordered) {
    case false:
      return `border: none;`
    default:
      return `
        border: 1px solid ${borderColor};
        :focus {
          outline: none;
          border: 1px solid ${borderActiveColor};
        }
      `
  }
}
const FloatLabel = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  &:focus-within label {
    transform: translate(18px, 8px) scale(0.95);
  }
`

const Label = styled.label`
  opacity: 50%;
  pointer-events: none;
  position: absolute;
  transform: translate(18px, 8px) scale(0.95);
`

const Wrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  margin: ${(props) => props.margin};
  ${(props) => (props.block ? 'width: 100%;' : '')}
`
const TextAreaWrapper = styled.div`
  display: inline-flex;
  position: relative;
  align-items: center;
`
const HelperTextWrapper = styled.div`
  min-height: 24px;
`
const Textarea = styled.textarea.attrs((props) => ({
  id: props.id,
  disabled: props.disabled,
  maxlength: props.maxLength,
  placeholder: props.placeholder,
  rows: props.rows,
  required: props.required,
}))`
  ${(props) =>
    setBorder(props.bordered, props.borderActiveColor, props.error)}
  padding: ${(props) => props.padding};
  font-size: ${(props) => props.fontSize};
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  background-color: ${(props) => props.backgroundColor};
  color: ${(props) => props.color};
  resize: ${(props) => props.resize};
  border-radius: ${theme.radius.base};
  box-sizing: border-box;
  ::placeholder {
    color: ${theme.colors.black50};
  }
  :disabled {
    background-color: #f4f4f4;
  }
  font-family: 'Noto Sans', sans-serif;
`

interface ITextArea {
  id?: string
  name?: string
  disabled?: boolean
  maxLength?: string
  placeholder?: string
  required?: boolean
  bordered?: boolean
  fontSize?: string
  block?: boolean
  width?: string
  height?: string
  margin?: string
  padding?: string
  backgroundColor?: string
  color?: string
  resize?: string
  helperText?: string
  error?: boolean
  value?: string
  onChange?: Function
  hideHelperText?: boolean
  floatingLabel?: string
  borderActiveColor?: string
  rows?: string
  countingText?: boolean
}

export default function TextArea({
  id,
  name = '',
  disabled,
  maxLength,
  placeholder,
  required,
  bordered = true,
  fontSize = '16px',
  block = false,
  width = '100%',
  height = 'auto',
  margin = '',
  floatingLabel,
  padding = floatingLabel ? '28px 18px 8px' : '18px',
  borderActiveColor = `${theme.colors.orange}`,
  backgroundColor = `${theme.colors.white}`,
  color = `${theme.colors.black}`,
  resize = 'vertical',
  helperText = '',
  error = false,
  value,
  onChange,
  hideHelperText = false,
  countingText = false,
  rows = '2',
  ...props
}: ITextArea): JSX.Element {
  const [labelInput, setLabelInput] = useState('')
  const handleChangeText = useCallback((event) => {
    if (onChange) onChange(event)
  }, [])

  const content = (
    <Wrapper margin={margin} block={block}>
      <TextAreaWrapper>
        <Textarea
          data-testid="textarea-test"
          id={id}
          name={name}
          disabled={disabled}
          maxLength={maxLength}
          placeholder={labelInput}
          required={required}
          bordered={bordered}
          fontSize={fontSize}
          width={width}
          height={height}
          padding={padding}
          backgroundColor={backgroundColor}
          borderActiveColor={borderActiveColor}
          color={color}
          resize={resize}
          value={value}
          onChange={handleChangeText}
          error={error}
          rows={rows}
          {...props}
        />
      </TextAreaWrapper>
      {!hideHelperText && (
        <HelperTextWrapper>
          <Typography
            color={error ? 'red' : 'black'}
            fontSize="12px"
            lineHeight="24px"
          >
            {helperText}
          </Typography>
        </HelperTextWrapper>
      )}
      {countingText && (
        <Flex justifyContent="space-between">
          <Typography type="rcaption" color="grayDark">
            Maks. Karakter
          </Typography>
          <Typography type="rcaption" color="grayDark">
            {`${value?.length}/${maxLength}`}
          </Typography>
        </Flex>
      )}
    </Wrapper>
  )

  useEffect(() => setLabelInput(placeholder), [placeholder])

  return (
    <>
      {floatingLabel ? (
        <FloatLabel>
          {content}
          <Label>
            <Typography>{floatingLabel}</Typography>
          </Label>
        </FloatLabel>
      ) : (
        content
      )}
    </>
  )
}
